import React, { useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

function ReserveTekst({ variabele, waarde, onSaved }) {
  const [v, setV] = useState(waarde || "");
  const [busy, setBusy] = useState(false);
  const opslaan = async () => {
    if ((v || "").trim() === (waarde || "").trim()) return;
    setBusy(true);
    try {
      const { data } = await api.get("/campagnes/variabele-fallbacks");
      const fallbacks = { ...(data.fallbacks || {}), [variabele]: v.trim() };
      if (!v.trim()) delete fallbacks[variabele];
      await api.put("/campagnes/variabele-fallbacks", { fallbacks });
      toast.success(v.trim() ? `Reserve-tekst voor {{${variabele}}} opgeslagen` : "Reserve-tekst verwijderd");
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Opslaan mislukt"); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-1.5 mt-1">
      <span className="text-[11px] text-amber-800 whitespace-nowrap">Reserve-tekst:</span>
      <Input value={v} onChange={e => setV(e.target.value)} onBlur={opslaan} onKeyDown={e => { if (e.key === "Enter") e.currentTarget.blur(); }}
        placeholder='bv. "relatie"' className="h-6 text-xs bg-white max-w-[220px]" data-testid={`fallback-input-${variabele}`} />
      {busy && <Loader2 className="h-3 w-3 animate-spin text-amber-700" />}
    </div>
  );
}

export function VariabeleWaarschuwingen({ waarschuwingen, onSaved }) {
  const open = waarschuwingen.filter(w => !w.fallback && !w.inline_fallback);
  const opgevangen = waarschuwingen.filter(w => w.fallback || w.inline_fallback);
  return (
    <div className={`border rounded-xl p-4 ${open.length ? "bg-amber-50 border-amber-200" : "bg-emerald-50 border-emerald-200"}`} data-testid="bouwer-variabele-waarschuwingen">
      <h3 className={`text-sm font-semibold mb-1 flex items-center gap-2 ${open.length ? "text-amber-900" : "text-emerald-900"}`}>
        {open.length ? <AlertTriangle className="h-4 w-4 text-amber-600" /> : <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
        {open.length ? "Variabelen die leeg blijven" : "Lege variabelen opgevangen"}
      </h3>
      {open.length > 0 && <p className="text-[11px] text-amber-800 mb-2">Voor deze deelnemers blijft de variabele leeg. Stel een reserve-tekst in (geldt voor alle campagnes) of gebruik inline <code className="bg-amber-100 px-1 rounded">{"{{contact.voornaam|relatie}}"}</code>.</p>}
      <ul className="text-xs space-y-2">
        {open.map((w, i) => (
          <li key={`o-${w.stap_id}-${w.variabele}`} className="text-amber-900" data-testid={`variabele-waarschuwing-${w.stap_id}-${w.variabele}`}>
            <span className="font-medium">{w.stap_naam}</span> · <code className="bg-amber-100 px-1 rounded">{`{{${w.variabele}}}`}</code> ({w.label}) leeg bij <span className="font-semibold tabular-nums">{w.leeg}</span> van {w.totaal}
            {w.voorbeelden?.length ? <span className="text-amber-700"> — bv. {w.voorbeelden.join(", ")}</span> : null}
            <ReserveTekst key={`${w.variabele}:${w.fallback || ""}`} variabele={w.variabele} waarde={w.fallback} onSaved={onSaved} />
          </li>
        ))}
        {opgevangen.map((w, i) => (
          <li key={`f-${w.stap_id}-${w.variabele}`} className="text-emerald-900" data-testid={`variabele-opgevangen-${w.stap_id}-${w.variabele}`}>
            <CheckCircle2 className="h-3 w-3 inline mr-1 text-emerald-600" /><span className="font-medium">{w.stap_naam}</span> · <code className="bg-emerald-100 px-1 rounded">{`{{${w.variabele}}}`}</code> leeg bij {w.leeg} van {w.totaal}
            {w.inline_fallback ? <span className="text-emerald-700"> → inline reserve-tekst in de mail</span> : <span className="text-emerald-700"> → reserve-tekst “{w.fallback}”</span>}
            {!w.inline_fallback && <ReserveTekst key={`${w.variabele}:${w.fallback || ""}`} variabele={w.variabele} waarde={w.fallback} onSaved={onSaved} />}
          </li>
        ))}
      </ul>
    </div>
  );
}
